import { collection, deleteDoc, doc, getDocs, onSnapshot, query, setDoc, where } from 'firebase/firestore'
import type { BioItem } from '@shared/lib/bioItems'
import { getFirestoreInstance } from '../firebase'
import { getCurrentPlaceId } from '../placeConfig'
import { stripUndefined } from './stripUndefined'
import { getOrders } from './orders'
import { liveStartIso, nowMs } from './liveCacheWindow'
import { recordLiveCounts, recordSnapshotApplyMs } from './perfPulse'

export const ITEMS_UPDATED_EVENT = 'wortelogy-items-updated'

const itemsCacheByPlace: Record<string, BioItem[]> = {}
const historyItemsByPlace: Record<string, Map<string, BioItem>> = {}
let itemsUnsubscribe: (() => void) | null = null
let liveRecentRows: BioItem[] | null = null
let liveBackfillRows: BioItem[] = []
let lastItemBackfillKey = ''
const IN_BATCH = 30

function itemsRef(placeId: string) {
  return collection(getFirestoreInstance(), 'places', placeId, 'items')
}

function itemDoc(placeId: string, itemId: string) {
  return doc(getFirestoreInstance(), 'places', placeId, 'items', itemId)
}

function dispatchItemsUpdated(): void {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new CustomEvent(ITEMS_UPDATED_EVENT))
}

function itemFromDoc(id: string, data: Record<string, unknown>): BioItem {
  return { id, ...data } as BioItem
}

function mergeItemsById(...lists: BioItem[][]): BioItem[] {
  const map = new Map<string, BioItem>()
  for (const list of lists) {
    for (const item of list) map.set(item.id, item)
  }
  return [...map.values()]
}

function historyMap(placeId: string): Map<string, BioItem> {
  if (!historyItemsByPlace[placeId]) historyItemsByPlace[placeId] = new Map()
  return historyItemsByPlace[placeId]
}

function replaceHistoryItems(placeId: string, rows: BioItem[]): void {
  const hist = new Map<string, BioItem>()
  for (const item of rows) hist.set(item.id, item)
  historyItemsByPlace[placeId] = hist
}

function setPlaceCache(placeId: string, items: BioItem[]): void {
  itemsCacheByPlace[placeId] = items
}

function upsertPlaceCache(placeId: string, item: BioItem): void {
  const cache = itemsCacheByPlace[placeId] ?? []
  const idx = cache.findIndex((i) => i.id === item.id)
  if (idx >= 0) {
    const next = cache.slice()
    next[idx] = item
    setPlaceCache(placeId, next)
    return
  }
  setPlaceCache(placeId, [...cache, item])
}

function patchPlaceCache(placeId: string, id: string, patch: Partial<BioItem>): void {
  const cache = itemsCacheByPlace[placeId] ?? []
  const idx = cache.findIndex((i) => i.id === id)
  if (idx < 0) return
  const next = cache.slice()
  next[idx] = { ...next[idx]!, ...patch }
  setPlaceCache(placeId, next)
}

function removeFromPlaceCache(placeId: string, id: string): void {
  const cache = itemsCacheByPlace[placeId] ?? []
  setPlaceCache(
    placeId,
    cache.filter((i) => i.id !== id),
  )
  historyItemsByPlace[placeId]?.delete(id)
}

function emitLiveItems(placeId: string): void {
  if (liveRecentRows === null) return
  const started = nowMs()
  const live = mergeItemsById(liveRecentRows, liveBackfillRows)
  setPlaceCache(placeId, live)
  recordSnapshotApplyMs(nowMs() - started)
  recordLiveCounts({ items: live.length })
  dispatchItemsUpdated()
}

export async function fetchItemsForOrderIds(orderIds: string[]): Promise<BioItem[]> {
  const placeId = getCurrentPlaceId()
  const unique = [...new Set(orderIds.filter(Boolean))]
  if (unique.length === 0) return []
  const ref = itemsRef(placeId)
  const out: BioItem[] = []
  for (let i = 0; i < unique.length; i += IN_BATCH) {
    const chunk = unique.slice(i, i + IN_BATCH)
    const snap = await getDocs(query(ref, where('orderId', 'in', chunk)))
    for (const d of snap.docs) out.push(itemFromDoc(d.id, d.data() as Record<string, unknown>))
  }
  return out
}

async function backfillOpenOrderItems(placeId: string): Promise<boolean> {
  const startIso = liveStartIso()
  const have = new Set((liveRecentRows ?? []).map((i) => i.id))
  const olderOpenOrderIds = getOrders()
    .filter((o) => (o.datetime || '') < startIso)
    .map((o) => o.id)
    .sort()
  const key = olderOpenOrderIds.join(',')
  if (key === lastItemBackfillKey) return false
  lastItemBackfillKey = key
  if (olderOpenOrderIds.length === 0) {
    liveBackfillRows = []
    return true
  }
  void placeId
  const extra = await fetchItemsForOrderIds(olderOpenOrderIds)
  liveBackfillRows = extra.filter((i) => !have.has(i.id))
  return true
}

export async function refreshOpenOrderItemBackfill(): Promise<void> {
  const placeId = getCurrentPlaceId()
  if (liveRecentRows === null) return
  const changed = await backfillOpenOrderItems(placeId)
  if (changed) emitLiveItems(placeId)
}

export async function primeItemsCache(): Promise<void> {
  const placeId = getCurrentPlaceId()
  if (itemsUnsubscribe) {
    itemsUnsubscribe()
    itemsUnsubscribe = null
  }
  const startIso = liveStartIso()
  const recentQuery = query(itemsRef(placeId), where('createdAt', '>=', startIso))
  const snap = await getDocs(recentQuery)
  liveRecentRows = snap.docs.map((d) => itemFromDoc(d.id, d.data() as Record<string, unknown>))
  await backfillOpenOrderItems(placeId)
  emitLiveItems(placeId)
  itemsUnsubscribe = onSnapshot(
    recentQuery,
    (snapshot) => {
      liveRecentRows = snapshot.docs.map((d) => itemFromDoc(d.id, d.data() as Record<string, unknown>))
      emitLiveItems(placeId)
    },
    (err) => console.error('[items] recent subscribe failed', err),
  )
}

export function getItems(): BioItem[] {
  const placeId = getCurrentPlaceId()
  return itemsCacheByPlace[placeId] ?? []
}

export function getHistoryItems(): BioItem[] {
  const placeId = getCurrentPlaceId()
  return [...(historyItemsByPlace[placeId]?.values() ?? [])]
}

export function getItem(id: string): BioItem | undefined {
  if (!id) return undefined
  const live = getItems().find((i) => i.id === id)
  if (live) return live
  return historyMap(getCurrentPlaceId()).get(id)
}

export function getItemsForOrder(orderId: string): BioItem[] {
  if (!orderId) return []
  const live = getItems().filter((i) => i.orderId === orderId)
  const hist = getHistoryItems().filter((i) => i.orderId === orderId)
  return mergeItemsById(hist, live)
}

export function setHistoryItems(rows: BioItem[]): void {
  replaceHistoryItems(getCurrentPlaceId(), rows)
  dispatchItemsUpdated()
}

export async function mergeItemsIntoHistory(items: BioItem[]): Promise<void> {
  setHistoryItems(mergeItemsById(getHistoryItems(), items))
}

export async function fetchAndMergeItemsForOrderIds(orderIds: string[]): Promise<BioItem[]> {
  const rows = await fetchItemsForOrderIds(orderIds)
  await mergeItemsIntoHistory(rows)
  return rows
}

/**
 * Dual-write first-class BIO item docs.
 * Nested order.items remain kitchen UI truth; settlement prefers this cache.
 * Callers must only attach billId of unpaid/attachable bills (never paid bills).
 */
export function addItems(items: BioItem[]): void {
  if (items.length === 0) return
  const placeId = getCurrentPlaceId()
  for (const item of items) {
    const full = stripUndefined(item)
    upsertPlaceCache(placeId, full)
    setDoc(itemDoc(placeId, item.id), full).catch((e) =>
      console.error('Firestore addItems failed', item.id, e),
    )
  }
  dispatchItemsUpdated()
}

/** Merge-patch a single item doc (completed, cancelledAt, billId, etc.). */
export function updateItem(id: string, patch: Partial<BioItem>): void {
  if (!id) return
  const placeId = getCurrentPlaceId()
  const sanitized = stripUndefined(patch)
  patchPlaceCache(placeId, id, sanitized)
  const hist = historyMap(placeId)
  const existing = hist.get(id)
  if (existing) hist.set(id, { ...existing, ...sanitized })
  setDoc(itemDoc(placeId, id), sanitized, { merge: true }).catch((e) =>
    console.error('Firestore updateItem failed', id, e),
  )
  dispatchItemsUpdated()
}

/** Hard-delete item docs (pending edit line removals). */
export function deleteItems(ids: string[]): void {
  if (ids.length === 0) return
  const placeId = getCurrentPlaceId()
  let changed = false
  for (const id of ids) {
    if (!id) continue
    removeFromPlaceCache(placeId, id)
    changed = true
    deleteDoc(itemDoc(placeId, id)).catch((e) =>
      console.error('Firestore deleteItems failed', id, e),
    )
  }
  if (changed) dispatchItemsUpdated()
}

/** Reassign items to another unpaid bill (edit move / Phase 4 split prep). */
export function moveItemsToBill(itemIds: string[], billId: string): void {
  if (!billId || itemIds.length === 0) return
  for (const id of itemIds) {
    if (!id) continue
    updateItem(id, { billId })
  }
}

/** Upsert full BioItem docs then delete removed ids from a sync plan. */
export function applyItemSyncPlan(upserts: BioItem[], deleteIds: string[]): void {
  addItems(upserts)
  deleteItems(deleteIds)
}

export function dropItemHistoryOverlays(): void {
  const placeId = getCurrentPlaceId()
  if (!historyItemsByPlace[placeId] || historyItemsByPlace[placeId].size === 0) return
  historyItemsByPlace[placeId] = new Map()
  dispatchItemsUpdated()
}

export function isItemsPrimed(): boolean {
  const placeId = getCurrentPlaceId()
  return placeId in itemsCacheByPlace
}

export function clearItemsCache(): void {
  if (itemsUnsubscribe) {
    itemsUnsubscribe()
    itemsUnsubscribe = null
  }
  liveRecentRows = null
  liveBackfillRows = []
  lastItemBackfillKey = ''
  Object.keys(itemsCacheByPlace).forEach((k) => delete itemsCacheByPlace[k])
  Object.keys(historyItemsByPlace).forEach((k) => delete historyItemsByPlace[k])
}

if (typeof window !== 'undefined') {
  window.addEventListener('wortelogy-orders-updated', () => {
    void refreshOpenOrderItemBackfill()
  })
}
